/**
 * Wallet connection, on top of RainbowKit + wagmi.
 *
 * RainbowKit owns everything the user sees while connecting: which wallets
 * exist, whether each is installed, the QR code, and the deep link into a
 * mobile wallet's browser. The configuration for that lives in `lib/wagmi.ts`.
 *
 * What stays here is the small surface the rest of the portal actually asks
 * for — an account, the right chain, a wallet client to sign with, and the raw
 * provider for the one EIP-1193 call viem has no action for. That surface is
 * unchanged from the hand-rolled version this replaced, so `ClaimCard` and
 * `StakingPanel` did not have to be touched: those are the modules where a
 * mistake costs someone their tokens.
 *
 * This module never reads, requests, or stores a seed phrase or private key.
 * Signing happens entirely inside the user's wallet.
 */
import "@rainbow-me/rainbowkit/styles.css";
// The theme variables, which RainbowKit would otherwise inject at runtime in a
// <style> element the CSP drops. See the file's own header.
// Imported by the entry point instead, alongside globals.css — the stylesheet
// is document-wide and should not depend on this component being reached.

import { RainbowKitProvider } from "@rainbow-me/rainbowkit";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createWalletClient, custom, type Address, type EIP1193Provider, type WalletClient } from "viem";

import { observeWalletRequests } from "@/lib/wallet-diagnostics";
import { WagmiProvider, useAccount, useDisconnect } from "wagmi";

import { chain, walletRpcUrls } from "@/lib/chain.client";
import { CHAIN_ID, NETWORK_NAME } from "@/lib/config";
import { describeSwitchError } from "@/lib/errors";
import { wagmiConfig } from "@/lib/wagmi";
import {
  isMobileWalletBrowser,
  needsTrustWalletTestnetBrowser,
  trustWalletDappBrowserUrl,
  walletConnectPeerName,
} from "@/lib/wallet-connect";
import {
  switchWalletNetwork,
  WALLET_CHAIN_SWITCH_TIMEOUT_MS,
} from "@/lib/wallet-network";

type WalletState = {
  address: Address | null;
  chainId: number | null;
  connecting: boolean;
  switchingNetwork: boolean;
  error: string | null;
  networkSwitchUrl: string | null;
  isCorrectNetwork: boolean;
  disconnect: () => void;
  switchNetwork: () => void;
  clearError: () => void;
  getWalletClient: () => WalletClient;
  /** The connected provider, for the few raw EIP-1193 calls viem has no API for. */
  getProvider: () => EIP1193Provider | null;
};

const WalletContext = createContext<WalletState | null>(null);

/** A provider, tagged with the connector it was resolved from. */
type ResolvedProvider = { uid: string; provider: EIP1193Provider };

/**
 * Created once, outside the component. A client rebuilt on re-render throws
 * away every in-flight query, and wagmi routes its connection state through it.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Chain reads in this portal go through `browserPublicClient` directly,
      // so nothing here benefits from a background refetch — and each one
      // spends the RPC budget `/api/rpc` is metering.
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

type WagmiPersistApi = {
  hasHydrated: () => boolean;
  onFinishHydration: (listener: () => void) => () => void;
};

/**
 * Wagmi's SSR mode restores localStorage after the first client paint. Until
 * that finishes, a disconnected account is provisional and must not be shown
 * as a real "Hubungkan Wallet" state.
 */
function useWagmiHydrated(): boolean {
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    // Wagmi exposes this runtime API through its Zustand store, but narrows it
    // to `never` in configs whose storage type was inferred internally.
    const persist = (
      wagmiConfig._internal.store as unknown as { persist: WagmiPersistApi }
    ).persist;
    let active = true;
    const markHydrated = () => {
      if (active) setHydrated(true);
    };
    const unsubscribe = persist.onFinishHydration(markHydrated);

    // The provider may have hydrated before this child effect subscribed.
    if (persist.hasHydrated()) queueMicrotask(markHydrated);

    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  return hydrated;
}

function trustWalletSwitchUrl(provider: EIP1193Provider | null): string | null {
  if (
    !provider ||
    typeof window === "undefined" ||
    !isMobileWalletBrowser() ||
    !needsTrustWalletTestnetBrowser(provider, CHAIN_ID)
  ) {
    return null;
  }

  return trustWalletDappBrowserUrl(window.location.href);
}

export function WalletProvider({ children }: { children: ReactNode }) {
  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <RainbowKitProvider
          locale="id-ID"
          modalSize="compact"
          initialChain={chain}
          // Suppresses RainbowKit's un-nonced <style>, which the CSP blocks —
          // leaving the dialog laid out but entirely transparent. The variables
          // ship through the bundler instead, from `src/rainbowkit-theme.css`.
          theme={null}
          appInfo={{
            appName: "AsetQu",
            // The last screen before a signing prompt is the right place to
            // repeat that the portal never asks for a seed phrase.
            learnMoreUrl: "/security",
          }}
        >
          <WalletBridge>{children}</WalletBridge>
        </RainbowKitProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
}

/**
 * Adapts wagmi's hooks to the `useWallet()` shape the portal is written
 * against.
 */
function WalletBridge({ children }: { children: ReactNode }) {
  const { address, chainId, connector, isConnecting, isReconnecting } = useAccount();
  const { disconnect } = useDisconnect();
  const hydrated = useWagmiHydrated();

  const [error, setError] = useState<string | null>(null);
  const [resolved, setResolved] = useState<ResolvedProvider | null>(null);
  const [switchingNetwork, setSwitchingNetwork] = useState(false);
  const switchAttempt = useRef(0);

  /**
   * The connector's provider, resolved into state.
   *
   * `connector.getProvider()` is async, but both consumers need it
   * synchronously — `getWalletClient()` is called at the top of a transaction
   * handler, and awaiting there would put the wallet prompt outside the user
   * gesture that some mobile wallets require.
   */
  useEffect(() => {
    if (!connector || !address) return;

    let cancelled = false;
    void connector
      .getProvider()
      .then((value) => {
        if (!cancelled) {
          setResolved({ uid: connector.uid, provider: value as EIP1193Provider });
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [connector, address]);

  /**
   * Derived rather than cleared in the effect above, and tagged with the
   * connector it came from. Clearing it on disconnect would take an extra
   * render to land, and during that render `getWalletClient()` would still hand
   * back the wallet the user just switched away from — which is the one thing
   * this provider must never do.
   */
  const provider =
    connector && address && resolved?.uid === connector.uid ? resolved.provider : null;
  const networkSwitchUrl = trustWalletSwitchUrl(provider);

  const switchNetwork = useCallback(() => {
    if (switchingNetwork) return;

    const attempt = ++switchAttempt.current;
    setError(null);
    setSwitchingNetwork(true);

    void (async () => {
      let walletName = connector?.name ?? "Wallet";
      try {
        if (!connector) throw new Error("Wallet belum terhubung.");
        const activeProvider =
          provider ?? ((await connector.getProvider()) as EIP1193Provider | null);
        if (!activeProvider) throw new Error("Provider wallet tidak tersedia.");
        walletName = walletConnectPeerName(activeProvider) ?? walletName;

        if (!provider) {
          setResolved({ uid: connector.uid, provider: activeProvider });
        }

        if (trustWalletSwitchUrl(activeProvider)) {
          setError(
            describeSwitchError(
              new Error("Request chains are not supported"),
              walletName,
              NETWORK_NAME,
            ),
          );
          return;
        }

        const result = await switchWalletNetwork({
          connector,
          provider: activeProvider,
          chainId: CHAIN_ID,
          rpcUrls: walletRpcUrls,
        });
        if (switchAttempt.current !== attempt) return;

        if (result === "timeout") {
          setError(
            `${walletName} belum mengonfirmasi jaringan setelah ` +
              `${WALLET_CHAIN_SWITCH_TIMEOUT_MS / 1_000} detik. Jika wallet sudah ` +
              `berada di ${NETWORK_NAME}, kembali ke portal dan muat ulang. Jika ` +
              `belum, pilih jaringan tersebut secara manual di aplikasi wallet.`,
          );
        }
      } catch (err) {
        if (switchAttempt.current !== attempt) return;
        setError(describeSwitchError(err, walletName, NETWORK_NAME));
      } finally {
        if (switchAttempt.current === attempt) setSwitchingNetwork(false);
      }
    })();
  }, [switchingNetwork, connector, provider]);

  const disconnectWallet = useCallback(() => {
    switchAttempt.current += 1;
    setError(null);
    setSwitchingNetwork(false);
    disconnect();
  }, [disconnect]);

  /* Wrapped once, here, rather than at each call site: the transaction leaves
     through the wallet client's transport, and a wrapper applied anywhere else
     would watch a provider the transaction never touches. The wrapper reads
     `eth_sendTransaction` and `eth_estimateGas` parameters and refuses every
     signing method by name — see lib/wallet-diagnostics.ts. */
  const observed = useMemo(
    () => (provider ? observeWalletRequests(provider) : null),
    [provider],
  );

  const getWalletClient = useCallback((): WalletClient => {
    if (!address) throw new Error("Wallet belum terhubung.");
    if (!observed) throw new Error("Wallet tidak terdeteksi.");
    return createWalletClient({ account: address, chain, transport: custom(observed) });
  }, [observed, address]);

  const getProvider = useCallback(() => observed, [observed]);
  const clearError = useCallback(() => setError(null), []);

  const value = useMemo<WalletState>(
    () => ({
      address: address ?? null,
      chainId: chainId ?? null,
      connecting: !hydrated || isConnecting || isReconnecting,
      switchingNetwork,
      // A manual switch is success too. Hide the old failure as soon as wagmi
      // observes the target chain, even if the wallet rejected the original
      // request after completing it.
      error: chainId === CHAIN_ID ? null : error,
      networkSwitchUrl: chainId === CHAIN_ID ? null : networkSwitchUrl,
      isCorrectNetwork: chainId === CHAIN_ID,
      disconnect: disconnectWallet,
      switchNetwork,
      clearError,
      getWalletClient,
      getProvider,
    }),
    [
      address,
      chainId,
      hydrated,
      isConnecting,
      isReconnecting,
      switchingNetwork,
      error,
      networkSwitchUrl,
      disconnectWallet,
      switchNetwork,
      clearError,
      getWalletClient,
      getProvider,
    ],
  );

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}

export function useWallet(): WalletState {
  const context = useContext(WalletContext);
  if (!context) throw new Error("useWallet must be used inside <WalletProvider>");
  return context;
}
